// @spec SCF-005
// Auto-selectors utility for Zustand stores

import type { StoreApi, UseBoundStore } from 'zustand';

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

/**
 * Adds auto-generated selectors to a Zustand store.
 *
 * This utility creates a `.use` property on the store with individual
 * selectors for each state property, enabling type-safe property access
 * without manual selector functions.
 *
 * @param _store - The Zustand store to enhance
 * @returns The enhanced store with `.use` selectors
 *
 * @example
 * const useStoreBase = create<State>()((set) => ({
 *   count: 0,
 *   increment: () => set((s) => ({ count: s.count + 1 })),
 * }));
 *
 * const useStore = createSelectors(useStoreBase);
 *
 * // Usage in component
 * const count = useStore.use.count();
 * const increment = useStore.use.increment();
 */
export function createSelectors<S extends UseBoundStore<StoreApi<object>>>(
  _store: S
): WithSelectors<S> {
  const store = _store as WithSelectors<S>;
  store.use = {} as WithSelectors<S>['use'];

  for (const key of Object.keys(store.getState())) {
    (store.use as Record<string, () => unknown>)[key] = () =>
      store((state) => state[key as keyof typeof state]);
  }

  return store;
}
